// Поток событий пульта (задача D5; сервер — D3, `GET /api/v1/stream`, SSE).
//
// Поток не заменяет запросы, а подсказывает, что поменялось: статус Анны для пилюли, статусы строк «Готовности»,
// настройки запуска, ход диалога, счётчики «На проверку», прогон проверок. Состояния при подключении сервер не
// присылает — только перемены после него (контракт D3), поэтому на каждое открытие потока — и первое, и после
// переподключения — пульт перечитывает данные экранов GET-ом: иначе перемена между ответом GET и подпиской потерялась бы.
// Первая загрузка, которая в этот момент ещё в пути, тоже уходит заново: сервер мог прочитать её данные до подписки.
//
// - `createStream` — одно соединение `EventSource` и его жизнь:
//   - обрыв → своё переподключение через 1 → 2 → 4 … до 30 с (повтор браузера с `retry: 5000` не ждём: соединение
//     закрываем сами);
//   - событие `session_expired` → окно «Сессия истекла» (`emitSessionExpired`), поток закрыт до нового входа;
//   - вкладка снова видна, а потока нет (ноутбук спал, обрыв) → переподключение сразу, без ожидания (Review Focus 3).
// - `applyStreamEvent` раскладывает события по кэшу запросов; `connectStream` = поток + кэш + перечитывание на каждое
//   открытие.
// - `useStream()` — подключение в каркасе, пока вошли и окно «Сессия истекла» закрыто. Формы событий — контракт D3
//   (`src/console/contract/stream.ts`). Опрос раз в 60 с остаётся запасным путём, если поток недоступен.
import {
  STREAM_EVENT_TYPES,
  STREAM_SESSION_EXPIRED_EVENT,
  StreamEventSchema,
  type ReadinessSnapshot,
  type StreamEvent,
} from '@contract/index.js';
import type { Query, QueryClient } from '@tanstack/react-query';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { emitSessionExpired, useSessionExpired } from '@/auth/session';
import { useMe } from './queries/auth';
import { qk } from './keys';
export type { StreamEvent } from '@contract/index.js';

/** Адрес потока. */
export const STREAM_PATH = '/api/v1/stream';

/** Включён ли поток в пульте. Браузера без `EventSource` (и jsdom в тестах) это не касается: там потока нет вовсе. */
export const STREAM_ENABLED = true;

/** Первая пауза перед переподключением и потолок паузы, мс. */
export const STREAM_RETRY_FIRST_MS = 1000;
export const STREAM_RETRY_MAX_MS = 30_000;

export type StreamState = 'open' | 'reconnecting' | 'closed';

/** То, что потоку нужно от `EventSource`: подписка на события и закрытие. Настоящий `EventSource` подходит как есть. */
export interface StreamSource {
  addEventListener(type: string, listener: (event: Event) => void): void;
  close(): void;
}

/** Конструктор соединения: в пульте — `EventSource`, в тестах — подделка. */
export type EventSourceCtor = new (url: string, init?: EventSourceInit) => StreamSource;

export interface CreateStreamOptions {
  readonly url?: string;
  readonly eventSourceCtor?: EventSourceCtor;
  onEvent(e: StreamEvent): void;
  onState(s: StreamState): void;
}

/** Пауза перед `attempt`-м переподключением (с нуля): 1, 2, 4 … 30 с. */
export function retryDelayMs(attempt: number): number {
  return Math.min(STREAM_RETRY_FIRST_MS * 2 ** attempt, STREAM_RETRY_MAX_MS);
}

function parseEvent(data: unknown): StreamEvent | null {
  if (typeof data !== 'string') return null;
  try {
    const parsed = StreamEventSchema.safeParse(JSON.parse(data));
    return parsed.success ? parsed.data : null;
  } catch {
    return null;
  }
}

/** Соединение с потоком (см. шапку файла). `close()` — навсегда: без переподключений. */
export function createStream(o: CreateStreamOptions): { close(): void } {
  const Ctor = o.eventSourceCtor ?? EventSource;
  const url = o.url ?? STREAM_PATH;
  let source: StreamSource | null = null;
  let timer: ReturnType<typeof setTimeout> | null = null;
  let attempt = 0;
  let stopped = false;
  let state: StreamState | null = null;

  function setState(next: StreamState): void {
    if (state === next) return;
    state = next;
    o.onState(next);
  }

  function drop(): void {
    const current = source;
    source = null;
    current?.close();
  }

  function schedule(): void {
    if (stopped) return;
    const delay = retryDelayMs(attempt);
    attempt += 1;
    setState('reconnecting');
    timer = setTimeout(() => {
      timer = null;
      connect();
    }, delay);
  }

  function connect(): void {
    if (stopped) return;
    const es = new Ctor(url, { withCredentials: true });
    source = es;
    es.addEventListener('open', () => {
      if (source !== es) return;
      attempt = 0;
      setState('open');
    });
    es.addEventListener('error', () => {
      if (source !== es) return;
      drop();
      schedule();
    });
    for (const type of STREAM_EVENT_TYPES) {
      es.addEventListener(type, (event) => {
        if (source !== es) return;
        const parsed = event instanceof MessageEvent ? parseEvent(event.data) : null;
        if (parsed !== null) o.onEvent(parsed);
      });
    }
    es.addEventListener(STREAM_SESSION_EXPIRED_EVENT, () => {
      if (source !== es) return;
      stop();
      setState('closed');
      emitSessionExpired();
    });
  }

  function onVisibility(): void {
    if (document.visibilityState !== 'visible' || stopped || source !== null) return;
    if (timer !== null) clearTimeout(timer);
    timer = null;
    attempt = 0;
    connect();
  }

  function stop(): void {
    stopped = true;
    if (timer !== null) clearTimeout(timer);
    timer = null;
    drop();
    document.removeEventListener('visibilitychange', onVisibility);
  }

  document.addEventListener('visibilitychange', onVisibility);
  connect();
  return {
    close() {
      if (stopped) return;
      stop();
      setState('closed');
    },
  };
}

/** Новые статусы строк «Готовности» поверх снимка: раскладку экран держит сам (строки не прыгают, D7). */
function mergeReadiness(
  prev: ReadinessSnapshot,
  e: Extract<StreamEvent, { type: 'readiness' }>,
): ReadinessSnapshot {
  const changed = new Map(e.changed.map((change) => [change.id, change]));
  return {
    ...prev,
    verdict: e.verdict,
    checks: prev.checks.map((check) => {
      const change = changed.get(check.id);
      return change === undefined ? check : { ...check, status: change.status, whatRu: change.whatRu, checkedAt: change.checkedAt };
    }),
  };
}

/** Событие потока — в кэш запросов. */
export function applyStreamEvent(queryClient: QueryClient, e: StreamEvent): void {
  switch (e.type) {
    case 'status':
      queryClient.setQueryData(qk.status, e.status);
      return;
    case 'readiness':
      queryClient.setQueryData<ReadinessSnapshot>(qk.readiness, (prev) => (prev === undefined ? prev : mergeReadiness(prev, e)));
      // Строка целиком (шаги, «чем грозит», «ждёт») приходит запросом: в событии только статус и «что не так».
      void queryClient.invalidateQueries({ queryKey: qk.readiness });
      return;
    case 'launch':
      // Версия — строка настроек целиком: «Кнопки и стадии сделок» (Ruling 125) и выборы «Как Анна квалифицирует» (W3-6)
      // лежат в тех же настройках.
      void queryClient.invalidateQueries({ queryKey: qk.launch });
      void queryClient.invalidateQueries({ queryKey: qk.bitrixTargets });
      void queryClient.invalidateQueries({ queryKey: qk.settingChoices });
      return;
    case 'dialog':
      void queryClient.invalidateQueries({ queryKey: qk.dialog(e.id) });
      void queryClient.invalidateQueries({ queryKey: ['dialogs'] });
      return;
    case 'counters':
      queryClient.setQueryData(qk.reviewCounts, e.review);
      return;
    case 'eval':
      void queryClient.invalidateQueries({ queryKey: ['eval'] });
      return;
  }
}

/** Всё, что перечитывается на открытие потока: данные экранов, кроме «кто вошёл» (его поток не меняет). */
function exceptMe(query: { readonly queryKey: readonly unknown[] }): boolean {
  return query.queryKey[0] !== qk.me[0];
}

/** Первая загрузка ещё в пути: данных нет, запрос идёт. */
function firstLoadInFlight(query: Query): boolean {
  return exceptMe(query) && query.state.data === undefined && query.state.fetchStatus === 'fetching';
}

/**
 * Открылся поток — перечитать данные экранов. Идущий запрос с данными TanStack сам отменит и пошлёт заново, а первую
 * загрузку (данных ещё нет) — нет: отдаст тот же запрос, хотя сервер мог прочитать его данные до подписки на поток.
 * Такую загрузку не отменяем (кто её ждёт — экран, «Обновить», — получил бы ошибку отмены): пусть закончится и сразу
 * покажет данные, а потом её запрос уходит ещё раз.
 */
function rereadOnOpen(queryClient: QueryClient): void {
  const cache = queryClient.getQueryCache();
  const waiting = new Set(cache.findAll({ predicate: firstLoadInFlight }).map((query) => query.queryHash));
  void queryClient.invalidateQueries({ predicate: exceptMe });
  if (waiting.size === 0) return;
  const unsubscribe = cache.subscribe((event) => {
    const { query } = event;
    if (!waiting.has(query.queryHash)) return;
    if (event.type !== 'removed' && query.state.fetchStatus !== 'idle') return;
    waiting.delete(query.queryHash);
    if (event.type !== 'removed') void queryClient.invalidateQueries({ queryKey: query.queryKey, exact: true });
    if (waiting.size === 0) unsubscribe();
  });
}

/**
 * Поток и кэш: события — в кэш, на каждое открытие потока (первое и после переподключения) — перечитать данные экранов:
 * сервер присылает только перемены после подписки, а то, что случилось до неё, приходит GET-ом.
 */
export function connectStream(
  queryClient: QueryClient,
  o: { readonly url?: string; readonly eventSourceCtor?: EventSourceCtor; readonly onState?: (s: StreamState) => void } = {},
): { close(): void } {
  return createStream({
    ...(o.url === undefined ? {} : { url: o.url }),
    ...(o.eventSourceCtor === undefined ? {} : { eventSourceCtor: o.eventSourceCtor }),
    onEvent: (e) => {
      applyStreamEvent(queryClient, e);
    },
    onState: (s) => {
      if (s === 'open') rereadOnOpen(queryClient);
      o.onState?.(s);
    },
  });
}

/** `EventSource` браузера; нет его (jsdom в тестах, очень старый браузер) — `undefined`, и поток не открывается. */
function browserEventSource(): EventSourceCtor | undefined {
  return typeof globalThis.EventSource === 'function' ? globalThis.EventSource : undefined;
}

/**
 * Поток в каркасе пульта: пока вошли и окно «Сессия истекла» закрыто. Новый вход (другой человек) — новое соединение.
 * Нет `EventSource` — хук ничего не делает, данные обновляются опросом.
 */
export function useStream(o: { readonly enabled?: boolean; readonly eventSourceCtor?: EventSourceCtor } = {}): void {
  const queryClient = useQueryClient();
  const userId = useMe()?.user.id;
  const expired = useSessionExpired();
  const ctor = o.eventSourceCtor ?? browserEventSource();
  const wanted = (o.enabled ?? STREAM_ENABLED) && !expired;
  useEffect(() => {
    // Вошёл другой человек (`userId` сменился) — поток открывается заново, уже с его сессией и ролью.
    if (!wanted || ctor === undefined || userId === undefined) return undefined;
    const stream = connectStream(queryClient, { eventSourceCtor: ctor });
    return () => {
      stream.close();
    };
  }, [wanted, queryClient, ctor, userId]);
}
